import { getLocale, t, type Locale } from "./i18n";

export function localeToBcp47(locale: Locale): string {
  return locale === "tr" ? "tr-TR" : "en-US";
}

function dateTimeFormatter(locale: Locale): Intl.DateTimeFormat {
  const bcp47 = localeToBcp47(locale);
  if (locale === "tr") {
    return new Intl.DateTimeFormat(bcp47, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  }
  return new Intl.DateTimeFormat(bcp47, {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return dateTimeFormatter(getLocale()).format(date);
  } catch {
    return value;
  }
}

function formatDecimal(value: number, locale: Locale): string {
  return new Intl.NumberFormat(localeToBcp47(locale), {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  }).format(value);
}

export function formatDurationSeconds(seconds: number): string {
  if (!seconds || seconds < 0) {
    const zeroUnit = t("format.duration.hours");
    return `0${zeroUnit}`;
  }
  const locale = getLocale();
  const hUnit = t("format.duration.hours");
  const mUnit = t("format.duration.minutes");
  const sUnit = t("format.duration.seconds");

  if (seconds >= 3600) {
    const hours = seconds / 3600;
    const rounded = hours >= 10 ? Math.round(hours) : hours;
    return `${formatDecimal(rounded, locale)}${hUnit}`;
  }
  if (seconds >= 60) {
    return `${Math.round(seconds / 60)}${mUnit}`;
  }
  return `${Math.round(seconds)}${sUnit}`;
}

export type DeviceStatusBucket = "online" | "recent" | "stale" | "never";

export function deviceStatus(lastSeenAt: string | null | undefined): DeviceStatusBucket {
  if (!lastSeenAt) return "never";
  const seen = new Date(lastSeenAt).getTime();
  if (Number.isNaN(seen)) return "never";
  const delta = Date.now() - seen;
  if (delta < 5 * 60 * 1000) return "online";
  if (delta < 60 * 60 * 1000) return "recent";
  return "stale";
}

export function deviceStatusLabel(bucket: DeviceStatusBucket): string {
  return t(`devices.statusValue.${bucket}`);
}

export function minutesToHHMM(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function hhmmToMinutes(value: string): number {
  const [h, m] = value.split(":").map((n) => Number(n));
  if (Number.isNaN(h) || Number.isNaN(m)) return 0;
  return h * 60 + m;
}

export function weekdayLabel(weekday: number): string {
  return t(`weekdays.${weekday}`);
}
